import useApi from 'hooks/useApi';
import * as UiValidate from 'ui-validation/UiValidate';
import constants from 'utils/constants';
import { toLocalTime } from 'utils/dateUtils';
import { safeInt } from 'utils/CommonMethods';

const ALARM_TYPE_LABEL = { all: 'All', 1: 'Critical', 2: 'Warning', 3: 'Info' };

const ALARM_TYPE_LABEL_TO_INTL = {
  all: 'all',
  All: 'all',
  Critical: 'notiEventLegendCritical',
  Warning: 'notiEventLegendWarning',
  Info: 'notiEventLegendInfo',
  1: 'notiEventLegendCritical',
  2: 'notiEventLegendWarning',
  3: 'notiEventLegendInfo'
};

const NOTIFICATION_STATUS_LABEL_TO_INTL = {
  Read: 'notificationsStatusRead',
  Unread: 'notificationsStatusUnread'
};

const TIME_RANGE_LABEL_TO_INTL = {
  'Last Month': 'notificationsTimeLastMonth',
  'Last Week': 'notificationsTimeLastWeek',
  'Last 24Hrs': 'notificationsTimeLast24Hrs',
  'Last 8Hrs': 'notificationsTimeLast8Hrs'
};

const isSeverity = (value) => value === 'Critical' || value === 'Warning' || value === 'Info';

export const formatNotificationLabel = (label, formatMessage) => {
  if (!label || !formatMessage) return label || '';
  const intlId = ALARM_TYPE_LABEL_TO_INTL[label] || NOTIFICATION_STATUS_LABEL_TO_INTL[label] || TIME_RANGE_LABEL_TO_INTL[label];
  if (!intlId) return label;
  return formatMessage({ id: intlId, defaultMessage: label });
};

export const formatAlarmTypeLabel = (type, formatMessage) => formatNotificationLabel(type, formatMessage);

export const mapAlarmType = (alarmType, fallbackType) => {
  const key = String(alarmType ?? '')
    .trim()
    .toLowerCase();
  return ALARM_TYPE_LABEL[key] || (isSeverity(fallbackType) ? fallbackType : 'Info');
};

export const flattenNotificationListResponse = (data) => {
  const { details, info } = data || {};
  const notifications = [];

  if (details && typeof details === 'object' && !Array.isArray(details)) {
    const groupedLists = Object.values(details).filter((value) => Array.isArray(value));
    groupedLists.forEach((list) => list.forEach((notification) => notifications.push(notification)));
    if (notifications.length) return notifications;
  }

  if (Array.isArray(details)) {
    details.forEach((notification) => notifications.push(notification));
    if (notifications.length) return notifications;
  }

  if (Array.isArray(info)) {
    info.forEach((notification) => notifications.push(notification));
  }

  return notifications;
};

export const mapNotificationToAlarmRow = (notification) => {
  const row = mapNotificationToGridRow(notification);
  return {
    id: row.id,
    type: row.type,
    time: row.time,
    message: row.message,
    status: row.status,
    correlatable: row.correlatable,
    correlationId: row.correlationId,
    correlationCount: row.correlationCount,
    deviceName: row.deviceName,
    deviceType: row.deviceType
  };
};

/**
 * Parses a correlated (v1) notification list page for detail-panel child rows.
 */
export const parseCorrelatedNotificationPage = (data, mapRow = mapNotificationToGridRow, page = 0) => {
  let flat = flattenNotificationListResponse(data);
  if (!flat.length && data && Array.isArray(data.info) && data.info.length && data.info[0]?._id) {
    flat = data.info;
  }
  const items = flat.map(mapRow);
  const serverLimit =
    typeof data?.details?.muiPaginationLimit === 'number' && data.details.muiPaginationLimit > 0 ? data.details.muiPaginationLimit : 200;
  const totalCount = typeof data?.details?.count === 'number' ? data.details.count : null;
  const hasMore = totalCount === null ? items.length >= serverLimit : page * serverLimit + items.length < totalCount;
  return { items, hasMore };
};

export const mapNotificationToGridRow = (notification) => ({
  id: notification._id || notification.esNotificationId || notification.id,
  type: mapAlarmType(notification.alarmType, notification.type),
  time: toLocalTime(notification.createdAt || notification.alarmCreatedAt, notification.nasCode),
  networkName: notification.networkName || '-',
  orgName: notification.orgName || '-',
  deviceName: notification.deviceName,
  // Only the real device type. `notification.type` is a SEVERITY (mapAlarmType treats it as
  // 'Critical' | 'Warning' | 'Info'), so using it as a fallback here put a severity in the
  // device-type slot - visible in the Log Message header line and in the Alarms CSV export.
  deviceType: notification.deviceType || '',
  serialNo: notification.serialNo || '',
  message: notification.description || notification.message || '',
  status: String(notification.status) === '1' ? 'read' : 'unread',
  title: notification.deviceName || notification.networkName || 'System Notification',
  actions: notification,
  correlatable: Boolean(notification.correlatable),
  correlationId: notification.correlationId ?? null,
  correlationCount: notification.correlationCount ?? 0
});

/** Default list sort when the grid has no user-applied sort (global and device alarms). */
export const NOTIFICATION_LIST_DEFAULT_SORT = [
  { sortBy: 'alarmType', sortDir: 'asc' },
  { sortBy: 'createdAt', sortDir: 'desc' }
];

export const NOTIFICATION_LIST_SORT_FALLBACK_DIR = 'asc';

export const getDeviceAlarmContextFromStorage = () => {
  try {
    const device = JSON.parse(localStorage.getItem(constants.SELECTED_DEVICE) || '{}');
    const resolvedType = device?.deviceType;
    const serial = device?.serialNo || device?.serial;
    const deviceId =
      device?.id ||
      device?.deviceId ||
      localStorage.getItem('selectedDeviceId') ||
      ((resolvedType === 'PR' || resolvedType === 'BR') && serial ? serial : null);
    return { deviceId };
  } catch {
    return { deviceId: localStorage.getItem('selectedDeviceId') };
  }
};

export const getNotificationListTotalCount = (data) => {
  if (!data) return 0;
  const { details, info } = data;
  if (Array.isArray(details)) return details.length;
  if (details && typeof details === 'object') {
    if (typeof details.count === 'number') return details.count;
    return Object.values(details).reduce((sum, value) => sum + (Array.isArray(value) ? value.length : 0), 0);
  }
  if (Array.isArray(info)) return info.length;
  return 0;
};

export const parseAndValidateShareEmails = (raw) => {
  const normalized = String(raw || '').replace(/[;\n]+/g, ',');
  const parts = normalized
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);

  const seen = new Set();
  const duplicates = new Set();
  const emails = [];
  for (const part of parts) {
    const key = part.toLowerCase();
    if (seen.has(key)) {
      duplicates.add(part);
      continue;
    }
    seen.add(key);
    emails.push(part);
  }

  const invalid = emails.filter((email) => {
    const [, isValid] = UiValidate.nonregexTesting('shareEmail', email);
    return !isValid;
  });

  return { emails, invalid, duplicates: Array.from(duplicates) };
};

export const fetchCorrelatedNotifications = async (correlationId, page = 0, mapRow = mapNotificationToGridRow) => {
  const params = {
    version: 'v1',
    alarmStatus: 'all',
    page,
    searchText: '',
    correlationId: `?correlationId=${encodeURIComponent(correlationId)}`,
    skipParams: '',
    stackUuidParam: ''
  };
  const body = { data: [{ sortBy: 'createdAt', sortDir: 'desc' }] };
  const data = await useApi('SHOW_NOTIFICATION_LIST', params, body, []);
  return parseCorrelatedNotificationPage(data, mapRow, page);
};

export const shareSelectedUserAlarms = async (notificationIds, emails) => {
  const payload = { email: emails, notificationIds, esNotificationIds: notificationIds };
  return useApi('SHARE_SELECTED_USER_ALARM', '', { data: payload }, []);
};

/** Marks only the given severities ('1' | '2' | '3') as read; an empty list marks every alarm as read. */
export const markAlarmsAsRead = async (alarmTypes = []) => {
  const alarmTypeQuery = alarmTypes.length ? `?alarmType=${alarmTypes.join(',')}` : '';
  return useApi('MARK_ALL_AS_READ_NOTIFICATION', { alarmTypeQuery }, {}, []);
};

export const fetchTotalUnreadAlarmCount = async () => {
  const data = await useApi('GET_NOTIFICATION_ALARM_COUNT', '', '', []);
  if (!data?.response?.status) return null;
  const userInfo = data.userInfo || data.details?.userInfo || data.details || {};
  return safeInt(userInfo.totalUnreadUserAlarms ?? userInfo.totalUnreadAlarms ?? userInfo.totalUnread ?? userInfo.unreadCount);
};

const buildListGetParams = (page, limit, searchText, queryParamsArg, skipHistoryRef, { alarmStatus, deviceId, stackUuid }) => {
  const searchPart = searchText ? `?searchText=${encodeURIComponent(searchText)}` : '';
  const hasCorrelationId = queryParamsArg?.correlationId != null && queryParamsArg.correlationId !== '';
  const correlationIdPrefix = searchPart ? '&' : '?';
  const correlationId = hasCorrelationId ? correlationIdPrefix + `correlationId=${encodeURIComponent(queryParamsArg.correlationId)}` : '';

  let skipParams = '';
  if (!hasCorrelationId && page > 0) {
    const skips = skipHistoryRef.current[page] ?? { correlatedSkip: 0, legacySkip: 0 };
    const correlatedSkip = skips.correlatedSkip ?? 0;
    const legacySkip = skips.legacySkip ?? 0;
    const skipPrefix = searchPart || correlationId ? '&' : '?';
    skipParams = `${skipPrefix}correlatedSkip=${correlatedSkip}&legacySkip=${legacySkip}`;
  }

  const stackUuidPart = stackUuid
    ? `${searchPart || correlationId || skipParams ? '&' : '?'}stackUuid=${encodeURIComponent(stackUuid)}`
    : '';

  const params = {
    version: hasCorrelationId ? 'v1' : 'v2',
    alarmStatus,
    page,
    limit,
    searchText: searchPart,
    correlationId,
    skipParams,
    stackUuidParam: stackUuidPart
  };

  if (deviceId != null && deviceId !== '') {
    params.deviceId = deviceId;
  }

  return params;
};

const buildListGetBody = (sortModel, sort) => {
  const { defaultSort, fieldMap, fallbackSortDir = 'desc' } = sort;
  if (!Array.isArray(sortModel) || sortModel.length === 0) return { data: defaultSort };
  const requestedField = sortModel[0]?.field;
  const requestedDir = sortModel[0]?.sort;
  const sortBy = fieldMap[requestedField] || requestedField;
  const sortDir = requestedDir === 'asc' || requestedDir === 'desc' ? requestedDir : fallbackSortDir;
  return { data: [{ sortBy, sortDir }] };
};

/**
 * Shared SmartDataGrid apiConfig for global and device notification/alarm lists.
 */
export const createNotificationListApiConfig = ({
  apiKey,
  userPreferenceAPIKey,
  skipHistoryRef,
  mapRow,
  alarmStatus = 'all',
  deviceId,
  stackUuid,
  sort,
  useMockData = false
}) => ({
  apiKey,
  userPreferenceAPIKey,
  useMockData,
  // Pagination here is a per-page skip cursor (skipHistoryRef below), not a page*pageSize
  // offset — tells SmartDataGrid to reset to page 1 on sort change.
  usesSkipCursorPagination: true,
  pagerSize: (data) => data?.details?.muiPaginationLimit || 200,
  transformResponse: (data) => flattenNotificationListResponse(data).map(mapRow),
  getTotalCount: getNotificationListTotalCount,
  getParams: (page, limit, searchText, sortModel, filters, queryParamsArg) =>
    buildListGetParams(page, limit, searchText, queryParamsArg, skipHistoryRef, { alarmStatus, deviceId, stackUuid }),
  getBody: (sortModel) => buildListGetBody(sortModel, sort),
  getHeaders: () => [],
  onRawDataLoaded: (rawData, serverPage) => {
    const nextCorrelatedSkip = rawData?.details?.nextCorrelatedSkip;
    const nextLegacySkip = rawData?.details?.nextLegacySkip;
    if (nextCorrelatedSkip === undefined || nextLegacySkip === undefined) return;
    skipHistoryRef.current[serverPage + 1] = { correlatedSkip: nextCorrelatedSkip, legacySkip: nextLegacySkip };
  },
  onReset: () => {
    skipHistoryRef.current = { 0: { correlatedSkip: 0, legacySkip: 0 } };
  }
});
